import { useCallback } from "react";
import omit from "lodash/omit";

import useRouter from "@stadium/hooks/lib/use-custom-route";

import { isForceTranslationsEnabled, setWeglotLangCookie } from "@stadium/services/weglot/helpers";
import { useStoreContext } from "@components/store/provider";
import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";

import { HEADER_ANALYTICS_TARGET } from "@stadium/shared/src/constants/analytics/header";
import { ANALYTICS_EVENT_ACTION_CHANGED } from "@stadium/services/analytics/constants/default";

export const useLanguageChange = () => {
  const router = useRouter();
  const { push, pathname } = router;
  const withAnalytics = useWithAnalytics();

  const { countryCode, lang, isNewRoute } = useStoreContext();

  const changeLang = useCallback(
    (selectedLang) => {
      if (lang !== selectedLang) {
        withAnalytics(undefined, {
          track: true,
          action: ANALYTICS_EVENT_ACTION_CHANGED,
          target: HEADER_ANALYTICS_TARGET.LANGUAGE,
          eventProperties: { language: selectedLang },
        })();

        setWeglotLangCookie(selectedLang);
        const hasLang = Object.keys(router.query).includes("lang");
        if (!hasLang) return;
        const forceTranslations = isForceTranslationsEnabled(router.query);
        const restQuery = forceTranslations ? omit(router.query, ["meta"]) : router.query;
        const updatedQuery = {
          ...restQuery,
          ...(isNewRoute
            ? { countryCode: `${countryCode}-${selectedLang}` } // countryCode naming will be changed to localeCode once we switch to new routes directories completely
            : { lang: selectedLang }),
        };
        let updatedPathname = pathname;
        if (updatedPathname === "/" && updatedQuery.countryCode) {
          updatedPathname = "/[countryCode]";
        }
        push({
          pathname: updatedPathname,
          query: updatedQuery,
        });
      }
    },
    [lang, push, pathname, router.query, isNewRoute, countryCode, withAnalytics],
  );
  return { changeLang, langInUrl: lang };
};
